import { useEffect, useRef } from "react";
import type { PendingConversation } from "../types/pending-conversation";
import type { Message } from "../types/message";
import type { ConversationStatus } from "../types/enum/conversation-status.enum";
import socket from "../services/socket";
import { SOCKET_EVENTS } from "../services/socket/events";

type UseSocketProps = {
  tenantId: string;
  userId: string;
  conversationIds?: string[];
  onReconnect?: () => void | Promise<void>;
  onConnectError?: (message: string) => void;
  onPendingConversation: (conversation: PendingConversation) => void;
  onAiAttendanceConversation?: (conversation: PendingConversation) => void;
  onTakenConversation: (conversationId: string) => void;
  onConversationClosed?: (
    conversationId: string,
    status: ConversationStatus,
  ) => void;
  onUserSentMessages: (conversationId: string, messages: Message[]) => void;
  onAttendantMessages: (
    conversationId: string,
    messages: Message[],
    senderId?: string,
    senderSocketId?: string,
  ) => void;
};

export function useSocket({
  tenantId,
  userId,
  conversationIds = [],
  onReconnect,
  onConnectError,
  onPendingConversation,
  onAiAttendanceConversation,
  onTakenConversation,
  onConversationClosed,
  onUserSentMessages,
  onAttendantMessages,
}: UseSocketProps) {
  const joinedConversationIdsRef = useRef<Set<string>>(new Set());
  const lastContextRef = useRef<{ tenantId?: string; userId?: string }>({});
  const lastSocketIdRef = useRef<string | undefined>(undefined);
  const hasConnectedAtLeastOnceRef = useRef(false);

  useEffect(() => {
    if (!socket || !tenantId || !userId) return;

    if (
      lastContextRef.current.tenantId !== tenantId ||
      lastContextRef.current.userId !== userId
    ) {
      joinedConversationIdsRef.current.clear();
      lastContextRef.current = { tenantId, userId };
    }

    const handleConnect = () => {
      const wasConnectedBefore = hasConnectedAtLeastOnceRef.current;
      hasConnectedAtLeastOnceRef.current = true;
      const socketIdChanged = lastSocketIdRef.current !== socket.id;

      // A reconexao gera um novo socket.id; nesse caso, todas as salas precisam de rejoin.
      if (socketIdChanged) {
        joinedConversationIdsRef.current.clear();
        lastSocketIdRef.current = socket.id;
      }

      socket.emit(SOCKET_EVENTS.ATTENDANT_JOIN_ROOM, { tenantId });
      conversationIds.forEach((conversationId) => {
        if (!joinedConversationIdsRef.current.has(conversationId)) {
          socket.emit(SOCKET_EVENTS.ATTENDANT_JOIN_CONVERSATION_ROOM, {
            tenantId,
            conversationId,
          });
          joinedConversationIdsRef.current.add(conversationId);
        }
      });

      if (wasConnectedBefore && socketIdChanged && onReconnect) {
        void onReconnect();
      }
    };

    const handleDisconnect = () => {
      joinedConversationIdsRef.current.clear();
      lastSocketIdRef.current = undefined;
    };

    const handleConnectError = (err: Error) => {
      if (!onConnectError) return;
      const message =
        typeof err?.message === "string" && err.message.trim()
          ? err.message
          : "Falha ao conectar ao realtime.";
      onConnectError(message);
    };

    if (socket.connected) {
      handleConnect();
    }

    socket.on(SOCKET_EVENTS.CONNECT, handleConnect);
    socket.on(SOCKET_EVENTS.DISCONNECT, handleDisconnect);
    socket.on("connect_error", handleConnectError);

    return () => {
      socket.off(SOCKET_EVENTS.CONNECT, handleConnect);
      socket.off(SOCKET_EVENTS.DISCONNECT, handleDisconnect);
      socket.off("connect_error", handleConnectError);
    };
  }, [tenantId, userId, conversationIds, onReconnect, onConnectError]);

  useEffect(() => {
    if (!socket) return;

    const handlePendingConversation = (payload: {
      data: PendingConversation;
    }) => {
      if (payload.data.tenantId && payload.data.tenantId !== tenantId) {
        return;
      }

      if (payload.data.status === "WAITING_HUMAN_ATTENDANCE") {
        onPendingConversation(payload.data);
        return;
      }

      if (
        payload.data.status === "AI_ATTENDANCE" &&
        onAiAttendanceConversation
      ) {
        onAiAttendanceConversation(payload.data);
      }
    };

    const handleTakenConversation = (payload: { conversationId: string }) => {
      console.log("Pegando conversa");

      onTakenConversation(payload.conversationId);
    };

    const handleUserSentMessage = (payload: {
      conversationId: string;
      sender: string;
      messages: Message[];
    }) => {
      console.log(" OPA");

      onUserSentMessages(payload.conversationId, payload.messages);
    };

    const handleAttendantMessage = (payload: {
      conversationId: string;
      sender: string;
      messages: Message[];
      senderSocketId?: string;
    }) => {
      onAttendantMessages(
        payload.conversationId,
        payload.messages,
        payload.sender,
        payload.senderSocketId,
      );
    };

    const handleConversationClosed = (payload: {
      conversationId: string;
      status: ConversationStatus;
    }) => {
      if (!onConversationClosed) return;
      onConversationClosed(payload.conversationId, payload.status);
    };

    socket.on(SOCKET_EVENTS.PENDING_CONVERSATION, handlePendingConversation);
    socket.on(SOCKET_EVENTS.TAKEN_CONVERSATION, handleTakenConversation);
    socket.on(SOCKET_EVENTS.USER_SENT_MESSAGE, handleUserSentMessage);
    socket.on(SOCKET_EVENTS.ATTENDANT_MESSAGE, handleAttendantMessage);
    socket.on(SOCKET_EVENTS.CONVERSATION_CLOSED, handleConversationClosed);

    return () => {
      socket.off(SOCKET_EVENTS.PENDING_CONVERSATION, handlePendingConversation);
      socket.off(SOCKET_EVENTS.TAKEN_CONVERSATION, handleTakenConversation);
      socket.off(SOCKET_EVENTS.USER_SENT_MESSAGE, handleUserSentMessage);
      socket.off(SOCKET_EVENTS.ATTENDANT_MESSAGE, handleAttendantMessage);
      socket.off(SOCKET_EVENTS.CONVERSATION_CLOSED, handleConversationClosed);
    };
  }, [
    tenantId,
    onPendingConversation,
    onAiAttendanceConversation,
    onTakenConversation,
    onConversationClosed,
    onUserSentMessages,
    onAttendantMessages,
    onReconnect,
    onConnectError,
  ]);
}
